/* Excavation geometry shared by server and client. A tunnel is a straight segment from its parent node,
   split into 1 px steps; carving step i removes a disc around point i. A chamber is an ellipse grown by a
   fill fraction. Replaying the same records always carves the same pixels. */

export const NS_EDGE = 0, NS_CHAMBER = 1, NS_DONE = 2;
export const F_SHAFT = 1, F_CROSS = 2, F_SUPPORT = 4, F_BUILT = 8;

export interface GNode {
  id: number; x: number; y: number; parent: number;
  state: number; prog: number; carved: number; cp: number;
  goal: number; type: number; r: number; flags: number; build: number; link: number; cargo: number;
  pts: [number, number][];
}

export const GOALS = ['root', 'depth', 'explore', 'chamber', 'shaft', 'target', 'cross', 'stope'] as const;
export const GOAL_CODE: Record<string, number> = Object.fromEntries(GOALS.map((g, i) => [g, i]));

export function segPts(px: number, py: number, x: number, y: number): [number, number][] {
  const L = Math.max(1, Math.ceil(Math.hypot(x - px, y - py))), pts: [number, number][] = [];
  for (let s = 0; s <= L; s++) pts.push([px + (x - px) * s / L, py + (y - py) * s / L]);
  return pts;
}
export const tunnelR2 = (flags: number) => (flags & F_SHAFT ? 16 : 9);

export type DigFn = (x: number, y: number) => void;
export function carveDisc(cx: number, cy: number, r2: number, dig: DigFn) {
  const r = Math.ceil(Math.sqrt(r2)), x0 = Math.round(cx), y0 = Math.round(cy);
  for (let dy = -r; dy <= r; dy++) for (let dx = -r; dx <= r; dx++) if (dx * dx + dy * dy <= r2) dig(x0 + dx, y0 + dy);
}
export const chamberRx = (r: number) => r * 1.6;
export function carveEllipse(x: number, y: number, r: number, k: number, dig: DigFn) {
  const rx = chamberRx(r) * k, ry = r * k; if (rx < 1) return;
  const cx = Math.round(x), cy = Math.round(y);
  for (let dy = -Math.ceil(ry); dy <= Math.ceil(ry); dy++) for (let dx = -Math.ceil(rx); dx <= Math.ceil(rx); dx++) {
    /* flattened floor so machinery sits level */
    if (dy > ry * .7) continue;
    if ((dx * dx) / (rx * rx) + (dy * dy) / (ry * ry) <= 1) dig(cx + dx, cy + dy);
  }
}
/* carve tunnel steps [from, to) of a node */
export function carveSteps(n: GNode, from: number, to: number, dig: DigFn) {
  const r2 = tunnelR2(n.flags);
  for (let i = from; i < to && i < n.pts.length; i++) carveDisc(n.pts[i][0], n.pts[i][1], r2, dig);
}

/* compact record: what the client needs to rebuild and draw a node */
export type NodeRec = number[];
export function nodeRec(n: GNode): NodeRec {
  return [n.id, n.x, n.y, n.parent, n.state, n.prog, n.carved, Math.round(n.cp * 1000), n.goal, n.type, Math.round(n.r * 100), n.flags, Math.round(n.build * 1000), n.link, n.cargo];
}
